/**
 * The app an address is inside, as the shell names it: the last segment of
 * the top bar's breadcrumb (`Amoreg / <company> / <app>`), the way Vercel
 * puts the project after the team. The segment opens the app's home; the
 * company segment before it leads back out.
 *
 * @module components/apps/AppBreadcrumb
 */
import { Link, useLocation } from "@tanstack/react-router";

import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrgId } from "../../state/orgs";
import { AppIcon, useAppEntries } from "./AppParts";
import { appDefinition, type AppEntry } from "./appRegistry";
import { appAreaFromPathname, type AppAreaId } from "./appsPaths";

/** Where each app area's name leads: its home page. */
export const APP_AREA_HOME = {
  imanage: "/$org/apps/imanage",
  "contract-intelligence": "/$org/apps/contract-intelligence",
  probe: "/$org/apps/probe/practice",
} as const satisfies Readonly<Record<AppAreaId, string>>;

export interface AppAreaHere {
  readonly area: AppAreaId;
  readonly orgId: string;
  readonly orgSegment: string;
  readonly pathname: string;
  readonly entry: AppEntry | null;
  readonly name: string;
}

/** The app area the current address is inside, with its name and icon; null outside one. */
export function useAppAreaHere(): AppAreaHere | null {
  const pathname = useLocation({ select: (location) => location.pathname });
  const orgId = useActiveOrgId();
  const area = appAreaFromPathname(pathname);
  const { entries } = useAppEntries(area === null ? null : orgId);
  if (!orgId || area === null) return null;
  const entry = entries.find((candidate) => candidate.id === area) ?? null;
  return {
    area,
    orgId,
    orgSegment: orgUrlSegment(orgId),
    pathname,
    entry,
    name: appDefinition(area)?.name ?? entry?.name ?? area,
  };
}

/** `/ [icon] Probe`, after the company in the top bar; nothing outside an app area. */
export function AppBreadcrumb() {
  const here = useAppAreaHere();
  if (here === null) return null;
  return (
    <>
      <span aria-hidden className="select-none text-base font-light text-sidebar-muted-foreground/60">
        /
      </span>
      <Link
        to={APP_AREA_HOME[here.area]}
        params={{ org: here.orgSegment }}
        data-app-breadcrumb={here.area}
        className="flex h-7 min-w-0 cursor-pointer items-center gap-2 rounded-md px-1.5 text-[13px] font-medium text-sidebar-foreground outline-hidden ring-ring transition-colors hover:bg-foreground/[0.07] focus-visible:ring-2"
      >
        {here.entry ? <AppIcon entry={here.entry} className="size-4.5 rounded-[5px]" /> : null}
        <span className="truncate">{here.name}</span>
      </Link>
    </>
  );
}
